import { useState } from 'react';
import { Link } from 'react-router';
import { useMeta } from '@/client/lib/useMeta';
import { api } from '@/client/lib/api';
import { track } from '@/client/lib/analytics';
import { SectionHeader } from '@/components/SectionHeader';
import { FramedImage } from '@/components/FramedImage';
import { WhatsAppIcon } from '@/components/Icons';
import { Turnstile, TURNSTILE_ENABLED } from '@/components/Turnstile';
import { BRAND, BUSINESS, businessAddressLine, businessAddressLineFr, REGIME_LABEL_FR } from '@/config/site';
import { useI18n } from '@/client/i18n/I18nContext';
import { staticPageText } from '@/lib/seo';
import { lp } from '@/client/i18n/locale';
import { ContactSourceField } from '@/components/ContactSourceField';
import { MESSAGE_MAX_LENGTH, withSource } from '@/lib/contactSource';

export function ContactPage() {
  const { t, locale } = useI18n();
  const fr = locale === 'fr';
  useMeta({ ...staticPageText('/contacto', locale), path: '/contacto' });
  const [form, setForm] = useState({ name: '', email: '', message: '', company: '', source: '' });
  const [status, setStatus] = useState<'idle' | 'loading' | 'done' | 'error'>('idle');
  const [tsToken, setTsToken] = useState('');
  const [tsReset, setTsReset] = useState(0);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (TURNSTILE_ENABLED && !tsToken) {
      setStatus('error');
      return;
    }
    setStatus('loading');
    try {
      const { source, ...fields } = form;
      // The origin line is for the owner's inbox: Spanish whatever the site locale.
      await api.contact({ ...fields, message: withSource(form.message, source), locale, turnstileToken: tsToken });
      track('contact', source ? { kind: 'contact', source } : { kind: 'contact' });
      setStatus('done');
    } catch {
      setStatus('error');
      setTsReset((n) => n + 1);
    }
  }

  return (
    <div className="container">
      <SectionHeader
        as="h1"
        eyebrow={t('contact.eyebrow')}
        title={t('contact.title')}
        lede={t('contact.lede')}
      />
      <div className="pdp" style={{ alignItems: 'start', paddingBottom: 'var(--space-8)' }}>
        {status === 'done' ? (
          <div className="caro-note">
            <p className="caro-note__body">{t('contact.thanks')}</p>
          </div>
        ) : (
          <form className="stack" onSubmit={onSubmit}>
            <div className="field">
              <label htmlFor="c-name">{t('common.name')}</label>
              <input id="c-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="field">
              <label htmlFor="c-email">{t('common.email')}</label>
              <input id="c-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div className="field">
              <label htmlFor="c-msg">{t('common.message')}</label>
              <textarea id="c-msg" rows={5} required maxLength={MESSAGE_MAX_LENGTH} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
            </div>
            <ContactSourceField id="c-source" value={form.source} onChange={(source) => setForm({ ...form, source })} />
            <div className="honeypot" aria-hidden>
              <input tabIndex={-1} autoComplete="off" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} />
            </div>
            <Turnstile onVerify={setTsToken} resetSignal={tsReset} />
            <button type="submit" className="btn" disabled={status === 'loading'}>
              {status === 'loading' ? t('common.sending') : t('contact.send')}
            </button>
            {status === 'error' ? <p style={{ color: 'var(--color-danger)' }}>{t('common.sendError')}</p> : null}
            <p className="text-muted" style={{ fontSize: 'var(--fs-xs)', maxWidth: '60ch' }}>
              {/* The label carries its colon (French spaces it). */}
              {t('contact.privacyResponsable')} {BUSINESS.legalName}. {t('contact.privacyNote')}{' '}
              <Link to={lp('/legal/privacidad')} className="link-underline" target="_blank" rel="noopener noreferrer">
                {t('contact.privacyLink')}
              </Link>.
            </p>
          </form>
        )}

        <aside className="stack">
          <div className="casa-card__media" style={{ aspectRatio: '4 / 5', marginBottom: 'var(--space-3)' }}>
            <FramedImage src="/media/caro/contacto-carolina-collaer-caro-casa-de-estilo.webp" alt="Carolina, CARO. Casa de estilo" fallback="CARO" />
          </div>
          <p className="eyebrow">{t('contact.alsoHere')}</p>
          <p>
            <a
              className="btn"
              href={BRAND.whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={t('contact.whatsappAria')}
              onClick={() => track('contact', { kind: 'whatsapp' })}
            >
              <WhatsAppIcon width={18} height={18} style={{ color: 'var(--color-white)', flexShrink: 0 }} />
              {t('contact.whatsappCta')}
            </a>
            <br />
            <span className="text-muted" style={{ fontSize: 'var(--fs-sm)' }}>{BRAND.whatsappDisplay}</span>
          </p>
          <p>
            {t('contact.emailLabel')}{' '}
            <a className="link-underline" href={`mailto:${BRAND.contactEmail}`}>{BRAND.contactEmail}</a>
          </p>
          <p>
            {t('contact.instagramLabel')}{' '}
            <a className="link-underline" href={BRAND.instagramUrl} target="_blank" rel="noopener noreferrer">
              @{BRAND.instagramHandle}
            </a>
          </p>
          <p className="text-muted">{t('common.hours')}</p>
          <p className="text-muted" style={{ fontSize: 'var(--fs-sm)' }}>
            {BUSINESS.legalName} ({fr ? REGIME_LABEL_FR : BUSINESS.regime}) · {BUSINESS.taxIdLabel} {BUSINESS.taxId}
            <br />
            {fr ? businessAddressLineFr() : businessAddressLine()}
          </p>
        </aside>
      </div>
    </div>
  );
}
